<script lang="ts">
	import {
		Button,
		ButtonSize,
		ButtonType,
		ReferenceTableRowsEditor
	} from '@repo/commons/components'
	import type { ReferenceTable, ReferenceTableRow } from '@repo/commons/models'
	import { confirmDelete, injectTranslation, showSaveFailedToast } from '@repo/commons/utils'
	import type { ResourceSchema } from '@semanticedge/persistence-service-noheaders-client/src'
	import { t } from 'svelte-i18n'
	import ConfiguredReferenceTablesList, {
		type ConfiguredTableRow
	} from './ConfiguredReferenceTablesList.svelte'
	import ReferenceTablePickerDialog from './ReferenceTablePickerDialog.svelte'

	type Props = {
		referenceTables: readonly ReferenceTable[]
		configuredTables: readonly ReferenceTable[]
		schemas: readonly ResourceSchema[]
		loading?: boolean
		sortable?: boolean
		onAdd: (table: ReferenceTable) => Promise<boolean>
		onDelete: (tableId: string) => Promise<boolean>
		onSaveRows: (table: ReferenceTable, rows: ReferenceTableRow[]) => Promise<boolean>
	}

	type ConfiguredCopy = {
		projectTable: ReferenceTable
		rows: readonly ReferenceTableRow[]
	}

	const {
		referenceTables,
		configuredTables,
		schemas,
		loading = false,
		sortable = false,
		onAdd,
		onDelete,
		onSaveRows
	}: Props = $props()

	let openTableId = $state<string | null>(null)
	let isPickerVisible = $state(false)

	const projectTablesById = $derived(new Map(referenceTables.map((table) => [table.id, table])))

	const schemasById = $derived(new Map(schemas.map((schema) => [schema.id, schema])))

	const copiesOfExistingTables: ConfiguredCopy[] = $derived(
		configuredTables.flatMap((copy) => {
			const projectTable = projectTablesById.get(copy.id)

			return projectTable?.id ? [{ projectTable, rows: copy.data ?? [] }] : []
		})
	)

	const tableRows: ConfiguredTableRow[] = $derived(
		copiesOfExistingTables.map(({ projectTable: { id = '', name = '', resourceSchemaId } }) => ({
			id,
			name,
			schemaName: schemasById.get(resourceSchemaId)?.name ?? ''
		}))
	)

	const configuredTableIds = $derived(tableRows.map(({ id }) => id))

	const openCopy = $derived(
		copiesOfExistingTables.find(({ projectTable }) => projectTable.id === openTableId)
	)

	const openSchema = $derived(schemasById.get(openCopy?.projectTable.resourceSchemaId))

	const openTable = (tableId: string): void => {
		openTableId = tableId
	}

	const closeTable = (): void => {
		openTableId = null
	}

	const openPicker = (): void => {
		isPickerVisible = true
	}

	const closePicker = (): void => {
		isPickerVisible = false
	}

	const handleAdd = async (table: ReferenceTable): Promise<void> => {
		if (!(await onAdd(table))) showSaveFailedToast()
	}

	const handleDelete = async (tableId: string): Promise<void> => {
		const confirmed = await confirmDelete(
			injectTranslation('Confirmation.delete_title', { entity: $t('Entity.reference_table') }),
			injectTranslation('InstanceReferenceTables.deleteMessage', {
				table: projectTablesById.get(tableId)?.name ?? ''
			})
		)

		if (!confirmed) return

		if (!(await onDelete(tableId))) showSaveFailedToast()
	}

	const saveOpenTableRows = (rows: ReferenceTableRow[]): Promise<boolean> =>
		openCopy ? onSaveRows(openCopy.projectTable, rows) : Promise.resolve(false)
</script>

<div class="content">
	{#if openCopy}
		<div class="editor-toolbar">
			<Button
				buttonText={$t('InstanceReferenceTables.back')}
				buttonType={ButtonType.TERTIARY}
				buttonSize={ButtonSize.MEDIUM}
				iconProps={{ name: 'arrowLeft', size: 'sm' }}
				onclick={closeTable}
			/>
		</div>

		{#key openCopy.projectTable.id}
			<ReferenceTableRowsEditor
				title={openCopy.projectTable.name ?? ''}
				rows={openCopy.rows}
				schema={openSchema}
				onSaveRows={saveOpenTableRows}
			/>
		{/key}
	{:else}
		<ConfiguredReferenceTablesList
			tables={tableRows}
			{loading}
			{sortable}
			onConfigure={openPicker}
			onOpen={openTable}
			onDelete={handleDelete}
		/>
	{/if}
</div>

<ReferenceTablePickerDialog
	visible={isPickerVisible}
	{referenceTables}
	{configuredTableIds}
	onAdd={handleAdd}
	onClose={closePicker}
/>

<style lang="postcss">
	.content {
		@apply flex flex-col gap-4;
	}

	.editor-toolbar {
		@apply flex items-center;
	}
</style>
